import { AccountWithUserAndToken, Token, api } from '@wellesley/openapi'
import { handleBasicResponse } from '@wellesley/openapi/helpers'
import { authSlice } from './authSlice'
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'

import { toast } from '@wellesley/system-toast'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { updateAccountData } from '@wellesley/host-remote/api/actions'
import { getErrorMsg } from '@wellesley/error-kit'
import { authToken } from './helpers/token'
import isInvalidCredsError from './helpers/isInvalidCredsError'

type SaveTokenParams<T extends Token | AccountWithUserAndToken> = {
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>
  queryFulfilled: Promise<{ data: T }>
  onSuccess?: () => void
  showInvalidCredsToast?: boolean
  showErrorToast?: boolean
}

const saveToken = async <T extends Token | AccountWithUserAndToken>({
  dispatch,
  queryFulfilled,
  onSuccess,
  showInvalidCredsToast = true,
  showErrorToast = true,
}: SaveTokenParams<T>) => {
  try {
    dispatch(authSlice.actions.setPending(true))
    const { data } = await queryFulfilled
    const token = 'sessionKey' in data ? data.sessionKey : data.token.sessionKey

    authToken.setToken(token)
    onSuccess?.()
    return queryFulfilled
  } catch (e) {
    const error = (e as { error: FetchBaseQueryError }).error
    const isInvalidCreds = isInvalidCredsError(error)

    dispatch(authSlice.actions.setPending(false))

    if (isInvalidCreds && !showInvalidCredsToast) return

    if (showErrorToast) toast.error(`${getErrorMsg(e as FetchBaseQueryError)}`)
  }
}

export const authApi = api.enhanceEndpoints({
  endpoints: {
    getApiV1Rules: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    postApiV1AccountsPasswordRecover: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    postApiV1AccountsPasswordRecoverCheckCode: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    postApiV1AccountsPasswordRecoverChange: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    postApiV1AccountsPasswordChangeConfirm: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(
          queryFulfilled,
          'Password successfully changed',
        )
      },
    },
    putApiV1AccountsPasswordChangeResendCode: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    postApiV1AccountsPasswordChangeRequest: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    putApiV1AccountsEmailChangeResendCode: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    putApiV1AccountsResendEmailByHash: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    postApiV1AccountsEmailChangeRequest: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    postApiV1AccountsEmailChangeConfirm: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled, 'Account email updated')
      },
    },
    postApiV1AccountsConfirmEmail: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    postApiV1AccountsCheckCaptcha: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    postApiV1AccountsFinishCreating: {
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await saveToken({
          dispatch,
          queryFulfilled,
          showErrorToast: false,
          onSuccess: () => {
            toast.success('User registered successfully')
          },
        })
      },
    },
    postApiV1AccountsAuthenticateEmail: {
      invalidatesTags: ['Secure'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await saveToken({
          dispatch,
          queryFulfilled,
          showInvalidCredsToast: false,
        })
      },
    },
    postApiV1AccountsSignup: {
      invalidatesTags: ['Secure'],
      // async onQueryStarted(_, { dispatch, queryFulfilled }) {
      //   await saveToken({ dispatch, queryFulfilled })
      // },
    },
    postApiV1AccountsUserReselectUser: {
      async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
        await saveToken({ dispatch, queryFulfilled })
        if (userId) {
          dispatch(updateAccountData({ selectedUserId: userId }))
          dispatch(api.util.invalidateTags(['USER_CHANGED']))
        }
      },
    },
    getApiV1RbacActorsByActorIdPermissions: {
      extraOptions: {
        initialDataKeyResolver: ({
          requestArgs,
        }: {
          requestArgs: string | { params?: unknown }
        }) => {
          if (typeof requestArgs === 'string') return 'userPermissions'

          const params = requestArgs.params
          if (!params || typeof params !== 'object') return 'userPermissions'

          const scope = (params as Record<string, unknown>).scope

          return typeof scope === 'string' && scope.length > 0
            ? 'currentUserGroupPermissions'
            : 'userPermissions'
        },
      },
      providesTags: ['RBAC'],
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
  },
})

export const {
  useGetApiV1RulesQuery,
  usePostApiV1AccountsPasswordRecoverMutation,
  usePostApiV1AccountsPasswordRecoverCheckCodeMutation,
  usePostApiV1AccountsPasswordRecoverChangeMutation,
  usePostApiV1AccountsPasswordChangeConfirmMutation,
  usePostApiV1AccountsPasswordChangeRequestMutation,
  usePutApiV1AccountsPasswordChangeResendCodeMutation,
  usePostApiV1AccountsAuthenticateEmailMutation,
  usePostApiV1AccountsEmailChangeRequestMutation,
  usePostApiV1AccountsEmailChangeConfirmMutation,
  usePostApiV1AccountsSignupMutation,
  usePutApiV1AccountsResendEmailByHashMutation,
  usePutApiV1AccountsEmailChangeResendCodeMutation,
  usePostApiV1AccountsConfirmEmailMutation,
  usePostApiV1AccountsFinishCreatingMutation,
  usePostApiV1AccountsUserReselectUserMutation,
  usePostApiV1AccountsCheckCaptchaMutation,
  useGetApiV1RbacActorsByActorIdPermissionsQuery,
} = authApi
